import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import TestSelector from "@/components/level3/TestSelector";
import TestInterface from "@/components/level3/TestInterface";
import { useLevel3Training } from "@/hooks/level3/useLevel3Training";
import { hasSavedExerciseData } from "@/hooks/level3/useAutoSave";
import { puttingTests } from "@/data/level3/puttingTests";
import { chippingTests } from "@/data/level3/chippingTests";
import { wedgingTests } from "@/data/level3/wedgingTests";
import { bunkerTests } from "@/data/level3/bunkerTests";
import { ironTests } from "@/data/level3/ironTests";
import { fairwayWoodTests } from "@/data/level3/fairwayWoodTests";
import { driverTests } from "@/data/level3/driverTests";
import { levels } from "@/data/level3/initialData";
import { getFormattedPoint } from "@/utils/level3/points";
import { getTableHeadTitle } from "@/utils/level3/titles";
import { getTestTitle, getTestDescription, getTestPoints, getTestDistances } from "@/services/Level3Service";
import { useFavorites } from "@/hooks/useFavorites";
import NavigationMenu from "@/components/layout/NavigationMenu";

const Level3Training = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshFavorites } = useFavorites();
  const { t } = useTranslation('training');
  
  const {
    scores,
    ballTendencies,
    selectedTest,
    currentPoint,
    currentAttempt,
    handleScoreChange,
    cancelLastEntry,
    handleFinish,
    handleSelectTest,
    handleBackToTests,
    getPointTotal,
    getGrandTotal
  } = useLevel3Training();

  const getTableHead = () => getTableHeadTitle(selectedTest);
  
  // Ne charger depuis l'URL que s'il n'y a pas de données sauvegardées
  useEffect(() => {
    const testParam = searchParams.get('test');
    const { hasData, testId: savedTestId } = hasSavedExerciseData();
    
    // Si des données sont sauvegardées, ne pas interférer - useAutoSave les restaurera
    if (hasData) {
      console.log('Données sauvegardées détectées, restauration en cours...');
      return;
    }
    
    if (testParam && !selectedTest) {
      const allTests = [...puttingTests, ...chippingTests, ...wedgingTests, ...bunkerTests, ...ironTests, ...fairwayWoodTests, ...driverTests];
      const testExists = allTests.some(test => test.id === testParam);
      
      if (testExists) {
        handleSelectTest(testParam);
      }
    }
  }, [searchParams, selectedTest]);
  
  useEffect(() => {
    if (selectedTest) {
      setSearchParams({ test: selectedTest });
    } else {
      setSearchParams({});
    }
  }, [selectedTest, setSearchParams]);

  useEffect(() => {
    refreshFavorites();
  }, [refreshFavorites]);

  const handleBackClick = () => {
    console.log('Navigating back to test selection from Level3Training');
    handleBackToTests();
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      <div className="container mx-auto py-8">
        <div className="flex justify-between mb-4">
          <div className="text-golf-600">
            <Link to="/training" className="text-green-600 hover:text-green-700 flex items-center gap-2">
              <ChevronLeft className="h-6 w-6" />
              <span>{t('level3.backToTraining')}</span>
            </Link>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-golf-800 mb-4">{t('level3.title')}</h1>
          <p className="text-xl text-golf-600">{t('level3.objective')}</p>
        </motion.div>

        {selectedTest === null ? (
          <TestSelector 
            puttingTests={puttingTests}
            chippingTests={chippingTests}
            wedgingTests={wedgingTests}
            bunkerTests={bunkerTests}
            ironTests={ironTests}
            fairwayWoodTests={fairwayWoodTests}
            driverTests={driverTests}
            onSelectTest={handleSelectTest}
          />
        ) : (
          <TestInterface 
            selectedTest={selectedTest}
            currentPoint={currentPoint}
            currentAttempt={currentAttempt}
            scores={scores}
            onBackToTests={handleBackClick}
            onFinish={handleFinish}
            onScoreChange={handleScoreChange}
            onCancelLastEntry={cancelLastEntry}
            getTestTitle={getTestTitle}
            getTestDescription={getTestDescription}
            getTestPoints={getTestPoints}
            getTestDistances={getTestDistances}
            getTableHeadTitle={getTableHead}
            getFormattedPoint={getFormattedPoint}
            getPointTotal={getPointTotal}
            getGrandTotal={getGrandTotal}
            levels={levels}
          />
        )}
      </div>
    </div>
  );
};

export default Level3Training;
